// Acoustics report payload mapper + Studio request helper.
// Browser global: window.AcousticsReportPayload
// Node tests: module.exports
(function (root, factory) {
  if (typeof module === "object" && module.exports) {
    module.exports = factory();
  } else {
    root.AcousticsReportPayload = factory();
  }
})(typeof window !== "undefined" ? window : globalThis, function () {
  "use strict";

  var DEFAULT_ENDPOINT =
    "https://studiov2.feltright.com/api/acoustics/report-requests";
  var TIER_DEFINITIONS = [
    { key: "starter", label: "Starter", percent: 40 },
    { key: "standard", label: "Standard", percent: 70 },
    { key: "premium", label: "Premium", percent: 100 },
  ];

  function nowIso() {
    return new Date().toISOString();
  }

  function makeReportNumber() {
    var timestamp = new Date()
      .toISOString()
      .replace(/[-:.TZ]/g, "")
      .slice(0, 14);
    var random = Math.random().toString(36).slice(2, 8).toUpperCase();
    return "ACR-" + timestamp + "-" + random;
  }

  function asNumber(value, fallback) {
    var n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  }

  function isFiniteNumber(value) {
    return value != null && Number.isFinite(Number(value));
  }

  function frequencyToHz(frequency) {
    return Number(String(frequency).replace(/[^0-9]/g, ""));
  }

  function labelFor(collection, value, fallback) {
    var item = (collection || []).find(function (entry) {
      return (
        entry &&
        (entry.value === value ||
          entry.id === value ||
          entry.product_id === value)
      );
    });
    return item && (item.label || item.name)
      ? item.label || item.name
      : fallback || value || "";
  }

  function productFor(app, surface, id) {
    var products =
      app && app.feltRightProducts && app.feltRightProducts[surface];
    return (products || []).find(function (product) {
      return product && product.id === id;
    });
  }

  function resolveProduct(app, surface, id, row) {
    if (app && typeof app.productForUnifiedBuildRow === "function" && row) {
      var unifiedProduct = app.productForUnifiedBuildRow(row);
      if (unifiedProduct) return unifiedProduct;
    }
    if (app && app.selectedProduct && app.selectedProduct.id === id) {
      return app.selectedProduct;
    }
    return productFor(app, surface, id) || {};
  }

  function productUnit(surface) {
    return surface === "ceiling" ? "baffles" : "tiles";
  }

  function absoluteProductUrl(value) {
    if (!value) return "";
    try {
      return new URL(value, "https://feltright.com").toString();
    } catch (_error) {
      return "";
    }
  }

  function getEngine(app) {
    if (app && app.acousticEngine) return app.acousticEngine;
    if (typeof window !== "undefined" && window.AcousticCalculatorEngine) {
      return window.AcousticCalculatorEngine;
    }
    return null;
  }

  function callCalculateRT60WithProducts(
    engine,
    frequency,
    wallQuantity,
    ceilingQuantity,
    ctx,
  ) {
    if (!engine || typeof engine.calculateRT60WithProducts !== "function")
      return null;
    if (engine.calculateRT60WithProducts.length >= 4) {
      return engine.calculateRT60WithProducts(
        frequency,
        wallQuantity,
        ceilingQuantity,
        ctx,
      );
    }
    return engine.calculateRT60WithProducts(
      frequency,
      wallQuantity,
      ceilingQuantity,
    );
  }

  function calculateExistingPoint(app, frequency) {
    var engine = getEngine(app);
    var ctx = app && app._engineCtx;
    var rt60 = null;

    if (
      engine &&
      ctx &&
      typeof engine.calculateExistingAbsorption === "function" &&
      typeof engine.calculateRT60 === "function"
    ) {
      var absorption = engine.calculateExistingAbsorption(frequency, ctx);
      rt60 = engine.calculateRT60(ctx.roomVolume, absorption);
    }

    if (!isFiniteNumber(rt60)) {
      rt60 = app && app.currentRT60;
    }

    return {
      frequency_hz: frequencyToHz(frequency),
      rt60_seconds: Number(asNumber(rt60, 0).toFixed(4)),
    };
  }

  function calculateTierPoint(app, frequency, tier) {
    var engine = getEngine(app);
    var baseCtx = app && app._engineCtx;
    var ctx = baseCtx ? Object.assign({}, baseCtx) : null;
    if (ctx && app && app.selectedProduct) {
      if (app.selectedProductSurface === "wall") {
        ctx.wallProduct = app.selectedProduct;
      } else if (app.selectedProductSurface === "ceiling") {
        ctx.ceilingProduct = app.selectedProduct;
      }
    }
    var quantity = Math.max(0, Math.round(asNumber(tier && tier.quantity, 0)));
    var surface = app && app.selectedProductSurface;
    var wallQuantity = surface === "wall" ? quantity : 0;
    var ceilingQuantity = surface === "ceiling" ? quantity : 0;
    var rt60 = callCalculateRT60WithProducts(
      engine,
      frequency,
      wallQuantity,
      ceilingQuantity,
      ctx,
    );

    if (!isFiniteNumber(rt60)) {
      rt60 = tier && tier.rt60ByFrequency && tier.rt60ByFrequency[frequency];
    }

    if (!isFiniteNumber(rt60)) {
      rt60 = tier && tier.rt60;
    }

    if (!isFiniteNumber(rt60)) {
      rt60 = app && app.effectiveTargetRT60;
    }

    return {
      frequency_hz: frequencyToHz(frequency),
      rt60_seconds: Number(asNumber(rt60, 0).toFixed(4)),
    };
  }

  function buildRecommendationGraphSeries(app, bands) {
    var tiers = app.singleProductTiers || {};
    return [
      {
        key: "existing",
        label: "Existing Condition",
        kind: "existing",
        points: bands.map(function (frequency) {
          return calculateExistingPoint(app, frequency);
        }),
      },
    ].concat(
      TIER_DEFINITIONS.map(function (definition) {
        return {
          key: definition.key,
          label: definition.label,
          kind: definition.key,
          points: bands.map(function (frequency) {
            return calculateTierPoint(app, frequency, tiers[definition.key]);
          }),
        };
      }),
    );
  }

  function legacyBuildRows(app) {
    var settings = app.settings || {};
    var rows = [];
    ["wall", "ceiling"].forEach(function (surface) {
      var key = "build_" + surface + "_products";
      (settings[key] || []).forEach(function (row) {
        rows.push(Object.assign({}, row, { surface: surface }));
      });
    });
    return rows;
  }

  function buildModeProductRows(app) {
    var settings = app.settings || {};
    var rows = Array.isArray(settings.build_products)
      ? settings.build_products
      : legacyBuildRows(app);
    return rows
      .map(function (row) {
        var quantity = asNumber(row && row.quantity, 0);
        if (quantity <= 0) return null;
        var surface = row.surface || "wall";
        var product = resolveProduct(app, surface, row.product_id, row);
        return {
          label: product.name || row.product_id || "",
          product_label: product.name || row.product_id || "",
          quantity_label: quantity + " " + productUnit(surface),
          surface: surface,
          values: {
            nrc: asNumber(product.nrc, 0),
            quantity: quantity,
          },
        };
      })
      .filter(Boolean);
  }

  function buildModeGraphSeries(app, bands) {
    var target = asNumber(app.effectiveTargetRT60, 0);
    return [
      {
        key: "existing",
        label: "Existing Condition",
        kind: "existing",
        points: bands.map(function (frequency) {
          return calculateExistingPoint(app, frequency);
        }),
      },
      {
        key: "selected-build",
        label: "Selected Products",
        kind: "selected",
        points: bands.map(function (frequency) {
          var rt60 = Number.isFinite(Number(app.buildModeRT60))
            ? app.buildModeRT60
            : target;
          if (typeof app.calculateBuildModeRT60 === "function") {
            rt60 = app.calculateBuildModeRT60(frequency);
          }
          return {
            frequency_hz: frequencyToHz(frequency),
            rt60_seconds: Number(asNumber(rt60, target).toFixed(4)),
          };
        }),
      },
      {
        key: "target",
        label: "Target",
        kind: "target",
        points: bands.map(function (frequency) {
          return {
            frequency_hz: frequencyToHz(frequency),
            rt60_seconds: Number(target.toFixed(4)),
          };
        }),
      },
    ];
  }

  function warningsFor(app) {
    return (app.warnings || [])
      .map(function (warning) {
        if (!warning) return "";
        return typeof warning === "string" ? warning : warning.message;
      })
      .filter(Boolean);
  }

  function buildContact(settings) {
    return {
      name: settings.name || settings.contact_name || "",
      company: settings.company || settings.contact_company || "",
      email: settings.email || "",
      phone: settings.phone || settings.contact_phone || "",
      project_name: settings.project_name || "Acoustics Calculator Request",
      project_location: settings.project_location || "",
    };
  }

  function buildRoomMaterials(app) {
    var settings = app.settings || {};
    var firstWall =
      settings.walls && settings.walls[0] && settings.walls[0].material_id;
    return {
      walls: labelFor(
        app.wallMaterials,
        firstWall,
        firstWall || "Not specified",
      ),
      ceiling: labelFor(
        app.ceilingMaterials,
        settings.ceiling_material,
        settings.ceiling_material || "Not specified",
      ),
      floor: labelFor(
        app.floorMaterials,
        settings.floor_material,
        settings.floor_material || "Not specified",
      ),
      carpeting:
        asNumber(settings.floor_covering_pct, 0) +
        "% carpet/soft floor coverage",
      open_entryways:
        asNumber(settings.open_entryways, 0) +
        " opening" +
        (asNumber(settings.open_entryways, 0) === 1 ? "" : "s"),
      available_wall_space: asNumber(settings.available_wall_space, 0) + "%",
      available_ceiling_space:
        asNumber(settings.available_ceiling_space, 0) + "%",
      furnishing_level: labelFor(
        app.furnitureOptions,
        settings.furniture,
        settings.furniture || "Not specified",
      ),
    };
  }

  function buildAcousticsReportPayload(app, options) {
    options = options || {};
    if (!app || !app.settings) {
      throw new Error(
        "Calculator app/settings are required to build an acoustics report payload",
      );
    }

    var settings = app.settings;
    var mode =
      settings.product_selection_mode === "build" ||
      settings.product_selection_mode === "build_your_space"
        ? "build_your_space"
        : "single_product";
    var bands = app.frequencyBands || [
      "250 Hz",
      "500 Hz",
      "1000 Hz",
      "2000 Hz",
      "4000 Hz",
    ];
    var payload = {
      version: 1,
      report_number: options.reportNumber || makeReportNumber(),
      completed_at: options.completedAt || nowIso(),
      calculator_mode: mode,
      contact: Object.assign(buildContact(settings), options.contact || {}),
      raw_settings: JSON.parse(JSON.stringify(settings)),
      room: {
        building_type: settings.building || "",
        room_type: settings.room || "",
        dimensions: {
          length_ft: asNumber(settings.room_length, 0),
          width_ft: asNumber(settings.room_width, 0),
          height_ft: asNumber(settings.room_height, 0),
        },
        materials: buildRoomMaterials(app),
      },
      reverb: {
        current_rt60_seconds: asNumber(app.currentRT60, 0),
        target_rt60_seconds: asNumber(
          app.effectiveTargetRT60,
          app.targetRT60 || 0,
        ),
        scale_min_seconds: 0,
        scale_max_seconds: Math.max(
          2.4,
          Math.ceil(asNumber(app.currentRT60, 0) * 10) / 10,
        ),
      },
      graph: {
        title: "Reverberation Time by Frequency Band",
        y_axis_label: "Reverberation Time (sec)",
        series:
          mode === "build_your_space"
            ? buildModeGraphSeries(app, bands)
            : buildRecommendationGraphSeries(app, bands),
      },
      warnings: warningsFor(app),
      disclaimers: [
        "This report is an acoustical estimate based on calculator inputs provided.",
        "Final placement and product quantities may vary after site review.",
      ],
    };

    if (mode === "build_your_space") {
      var buildRows = buildModeProductRows(app);
      payload.build_your_space = {
        wall_rows: buildRows.filter(function (row) {
          return row.surface === "wall";
        }),
        ceiling_rows: buildRows.filter(function (row) {
          return row.surface === "ceiling";
        }),
      };
    } else {
      var selectedProduct = app.selectedProduct || {};
      var tiers = app.singleProductTiers || {};
      payload.recommendation = {
        selected_product: {
          id: selectedProduct.id || settings.selected_product || "",
          name: selectedProduct.name || "",
          surface: app.selectedProductSurface || "",
          nrc: asNumber(selectedProduct.nrc, 0),
          url: absoluteProductUrl(selectedProduct.url),
        },
        tiers: TIER_DEFINITIONS.map(function (definition) {
          var tier = tiers[definition.key] || {};
          return {
            key: definition.key,
            label: definition.label,
            percent: definition.percent,
            quantity: Math.max(0, Math.round(asNumber(tier.quantity, 0))),
            rt60_seconds: asNumber(tier.rt60, 0),
          };
        }),
        selection: {
          key: "selection",
          label: "Your Selection",
          quantity: Math.max(
            0,
            Math.round(asNumber(app.singleProductSelection && app.singleProductSelection.quantity, 0)),
          ),
          rt60_seconds: asNumber(
            app.singleProductSelection && app.singleProductSelection.rt60,
            asNumber(app.desiredRT60, 0),
          ),
        },
      };
    }

    return payload;
  }

  async function createAcousticsReportRequest(app, options) {
    options = options || {};
    var fetchImpl =
      options.fetchImpl ||
      (typeof fetch !== "undefined" ? fetch.bind(globalThis) : null);
    if (!fetchImpl)
      throw new Error(
        "fetch is not available for acoustics report request creation",
      );

    var endpoint =
      options.endpoint ||
      (typeof window !== "undefined" &&
        window.acousticsReportRequestEndpoint) ||
      DEFAULT_ENDPOINT;
    var payload = buildAcousticsReportPayload(app, options);
    var response = await fetchImpl(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!response || !response.ok) {
      throw new Error("Unable to create acoustics report request");
    }

    var data = await response.json();
    if (!data || !data.report_request_id) {
      throw new Error(
        "Acoustics report request response did not include report_request_id",
      );
    }

    return {
      report_request_id: data.report_request_id,
      email_sent: data.email_sent,
      payload: payload,
    };
  }

  return {
    buildAcousticsReportPayload: buildAcousticsReportPayload,
    createAcousticsReportRequest: createAcousticsReportRequest,
  };
});
